import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { Job, PipelineAction, PipelineStage } from "@career-ops-amplify/shared";
import { listPrompts, type PromptTemplate } from "@/api/prompts";
import { PromptTemplatePicker } from "@/components/jobs/prompt-template-picker";
import { PromptPreviewModal } from "@/components/jobs/prompt-preview-modal";
import {
  STAGE_DESCRIPTIONS,
  STAGE_TRANSITIONS,
  type PipelineStageName,
  EVAL_KIND_ORDER,
} from "@/lib/pipeline-stages";
import { stagePromptCategory } from "@/lib/pipeline-stages";
import { extractVerdictLine, resolveJobScore } from "@/format";
import { JobScoreControl } from "@/components/jobs/job-score-control";
import { renderMarkdown } from "@/lib/markdown";
import { cn } from "@/cn";
import { runPipelinePrompt, advanceStage, recordAction } from "@/api/pipeline";
import { useUpdateJob } from "@/hooks/useJobs";
import { useContacts } from "@/hooks/useContacts";
import { ContactForm, ContactList } from "@/components/companies/contact-panel";
import { ContactoSavePanel } from "@/components/network/contacto-save-panel";
import { FollowUpPanel } from "@/components/network/followup-panel";

interface PipelineStagePanelProps {
  pipelineId: string;
  stageName: PipelineStageName;
  job: Job;
  stages: PipelineStage[];
  actions: PipelineAction[];
  currentStage?: string;
  onStageAdvanced?: (stage: PipelineStageName) => void;
}

function sortTemplatesForStage(stageName: PipelineStageName, templates: PromptTemplate[]): PromptTemplate[] {
  if (stageName !== "Evaluated") return templates;
  return [...templates].sort((a, b) => {
    const oa = EVAL_KIND_ORDER[a.kind] ?? 99;
    const ob = EVAL_KIND_ORDER[b.kind] ?? 99;
    if (oa !== ob) return oa - ob;
    return a.name.localeCompare(b.name);
  });
}

function defaultPromptKind(stageName: PipelineStageName, templates: PromptTemplate[]): string {
  if (templates.length === 0) return "";
  if (stageName === "Evaluated") {
    return templates.find((t) => t.kind === "evaluate")?.kind ?? templates[0].kind;
  }
  return templates[0].kind;
}

function stageOutputFromJob(stageName: PipelineStageName, job: Job): string | undefined {
  if (stageName === "Evaluated") return job.evaluation;
  if (stageName === "Applied") return job.notes;
  return undefined;
}

export function PipelineStagePanel({
  pipelineId,
  stageName,
  job,
  stages,
  actions,
  currentStage,
  onStageAdvanced,
}: PipelineStagePanelProps) {
  const qc = useQueryClient();
  const category = stagePromptCategory(stageName) ?? "";
  const updateJob = useUpdateJob(job.id);
  const { data: contactsData } = useContacts(
    stageName === "Responded" && job.companySlug
      ? { companySlug: job.companySlug }
      : undefined,
  );
  const stageContacts = contactsData?.contacts ?? [];

  const { data: templates = [], isLoading: templatesLoading } = useQuery({
    queryKey: ["prompts", "category", category],
    queryFn: async () => (await listPrompts({ category })).prompts,
    enabled: !!category,
    staleTime: 30_000,
  });

  const sortedTemplates = useMemo(
    () => sortTemplatesForStage(stageName, templates),
    [stageName, templates],
  );

  const [selectedPromptKind, setSelectedPromptKind] = useState("");
  const [previewPromptKind, setPreviewPromptKind] = useState<string | null>(null);
  const [runOutput, setRunOutput] = useState<string | undefined>(stageOutputFromJob(stageName, job));
  const [runError, setRunError] = useState("");
  const [stageNotes, setStageNotes] = useState(job.notes ?? "");
  const [rejectionReason, setRejectionReason] = useState(job.rejectionReason ?? "");
  const [offerDetails, setOfferDetails] = useState(
    job.offerDetails ? JSON.stringify(job.offerDetails, null, 2) : "",
  );

  useEffect(() => {
    if (!selectedPromptKind && sortedTemplates.length > 0) {
      setSelectedPromptKind(defaultPromptKind(stageName, sortedTemplates));
    }
  }, [sortedTemplates, selectedPromptKind, stageName]);

  useEffect(() => {
    setRunOutput(stageOutputFromJob(stageName, job));
    setStageNotes(job.notes ?? "");
    setRejectionReason(typeof job.rejectionReason === "string" ? job.rejectionReason : "");
    setOfferDetails(job.offerDetails ? JSON.stringify(job.offerDetails, null, 2) : "");
  }, [job, stageName]);

  const selectedTemplate = sortedTemplates.find((t) => t.kind === selectedPromptKind);
  const stageMeta = stages.find((s) => s.name === stageName);
  const stageActions = actions.filter((a) => a.stageId === stageName);
  const isCurrent = currentStage === stageName;
  const displayScore = resolveJobScore(job);
  const verdictLine = runOutput ? extractVerdictLine(runOutput) : undefined;

  const runMutation = useMutation({
    mutationFn: () =>
      runPipelinePrompt(pipelineId, { promptKind: selectedPromptKind, stageName }),
    onSuccess: (result) => {
      if (result.success && result.output) {
        setRunOutput(result.output);
        setRunError("");
        qc.invalidateQueries({ queryKey: ["pipeline"] });
        qc.invalidateQueries({ queryKey: ["jobs"] });
      } else {
        setRunError(result.error ?? "Run failed");
      }
    },
    onError: (err) => {
      setRunError(err instanceof Error ? err.message : "Run failed");
    },
  });

  const advanceMutation = useMutation({
    mutationFn: (target: PipelineStageName) => advanceStage(pipelineId, target),
    onSuccess: (_data, target) => {
      qc.invalidateQueries({ queryKey: ["pipeline"] });
      qc.invalidateQueries({ queryKey: ["jobs"] });
      onStageAdvanced?.(target);
    },
  });

  const saveNotesMutation = useMutation({
    mutationFn: async () => {
      const updates: Record<string, unknown> = {};
      if (stageName === "Rejected") updates.rejectionReason = rejectionReason;
      else if (stageName === "Offer") {
        try {
          updates.offerDetails = offerDetails.trim() ? JSON.parse(offerDetails) : null;
        } catch {
          throw new Error("Offer details must be valid JSON");
        }
      } else updates.notes = stageNotes;

      await updateJob.mutateAsync(updates as never);
      if (stageNotes.trim() || rejectionReason.trim()) {
        await recordAction(pipelineId, {
          type: "note",
          stageId: stageName,
          status: "done",
          result: (rejectionReason || stageNotes).slice(0, 200),
        });
      }
      qc.invalidateQueries({ queryKey: ["pipeline"] });
    },
  });

  const hasDescription = Boolean(job.jobDescription?.trim());
  const readiness = [
    { label: "Job description", ok: hasDescription },
    { label: "Company & role", ok: Boolean(job.company?.trim() && job.role?.trim()) },
    ...(stageName === "Evaluated"
      ? [{ label: "Evaluation run", ok: Boolean(job.evaluation?.trim()) }]
      : []),
  ];

  return (
    <div className="space-y-5">
      <div>
        <div className="mb-1 flex flex-wrap items-center gap-2">
          <h2 className="text-sm font-semibold text-foreground">{stageName}</h2>
          {isCurrent && (
            <span className="rounded-full bg-brand/10 px-2 py-0.5 text-[10px] font-medium text-brand">
              Current stage
            </span>
          )}
          {stageMeta?.status && (
            <span className="rounded-full bg-muted/30 px-2 py-0.5 text-[10px] text-muted-foreground capitalize">
              {stageMeta.status}
            </span>
          )}
        </div>
        <p className="text-xs text-muted-foreground">{STAGE_DESCRIPTIONS[stageName]}</p>
      </div>

      <div className="flex flex-wrap gap-2">
        {readiness.map((item) => (
          <span
            key={item.label}
            className={cn(
              "rounded-full px-2.5 py-0.5 text-[10px] font-medium",
              item.ok
                ? "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400"
                : "bg-amber-500/10 text-amber-600 dark:text-amber-400",
            )}
          >
            {item.label}: {item.ok ? "ready" : "needed"}
          </span>
        ))}
      </div>

      {stageName === "Evaluated" && (
        <>
          <JobScoreControl
            jobId={job.id}
            job={{
              score: job.score,
              fitScore: job.fitScore,
              evaluation: runOutput ?? job.evaluation,
              research: job.research,
            }}
          />
          {verdictLine && (
            <p className="rounded-lg border border-border bg-card px-4 py-3 text-sm text-muted-foreground">
              {verdictLine}
              {job.evaluationPromptKind && (
                <span className="ml-2 text-[10px] text-muted-foreground">
                  via {job.evaluationPromptKind}
                </span>
              )}
            </p>
          )}
        </>
      )}

      {category && (
        <section className="rounded-lg border border-border bg-card/50 p-4">
          <p className="mb-3 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
            Prompt library
          </p>

          {templatesLoading ? (
            <div className="flex items-center gap-2 py-4 text-xs text-muted-foreground">
              <div className="h-3 w-3 animate-spin rounded-full border-2 border-muted border-t-brand" />
              Loading templates…
            </div>
          ) : (
            <PromptTemplatePicker
              templates={sortedTemplates}
              selectedKind={selectedPromptKind}
              onSelect={setSelectedPromptKind}
              onViewPrompt={setPreviewPromptKind}
              disabled={runMutation.isPending}
              preserveOrder
              emptyMessage={`No prompts tagged with category "${category}". Reseed or add templates in Settings.`}
            />
          )}

          <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-xs text-muted-foreground">
              {selectedTemplate
                ? `Runs “${selectedTemplate.name}” with this job’s context.`
                : "Select a template to run."}
            </p>
            <div className="flex shrink-0 flex-wrap gap-2">
              {selectedPromptKind && (
                <button
                  type="button"
                  onClick={() => setPreviewPromptKind(selectedPromptKind)}
                  className="rounded-md border border-border px-3 py-2 text-xs font-medium text-foreground hover:bg-muted/20"
                >
                  View prompt
                </button>
              )}
              <button
                type="button"
                onClick={() => runMutation.mutate()}
                disabled={runMutation.isPending || !selectedPromptKind}
                className="rounded-md bg-brand px-4 py-2 text-xs font-medium text-brand-foreground hover:opacity-90 disabled:opacity-50"
              >
                {runMutation.isPending
                  ? "Running…"
                  : selectedTemplate
                    ? `Run ${selectedTemplate.name}`
                    : "Run"}
              </button>
            </div>
          </div>

          {runError && (
            <p className="mt-3 rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-xs text-destructive">
              {runError}
            </p>
          )}
        </section>
      )}

      {runOutput && (
        <section className="rounded-lg border border-border bg-card p-4">
          <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Output
          </h3>
          <div
            className="prose-sm max-h-[28rem] overflow-auto"
            dangerouslySetInnerHTML={{ __html: renderMarkdown(runOutput) }}
          />
          {selectedPromptKind === "contacto" && job.companySlug && (
            <ContactoSavePanel
              output={runOutput}
              companySlug={job.companySlug}
              jobId={job.id}
            />
          )}
        </section>
      )}

      {stageName === "Responded" && job.companySlug && (
        <section className="rounded-lg border border-border bg-card p-4">
          <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Contacts
          </h3>
          <ContactList contacts={stageContacts} emptyMessage="Add a recruiter or hiring manager before drafting follow-up." />
          <div className="mt-3">
            <ContactForm companySlug={job.companySlug} jobId={job.id} />
          </div>
          <div className="mt-4">
            <FollowUpPanel job={job} contacts={stageContacts} compact />
          </div>
        </section>
      )}

      {(stageName === "Applied" ||
        stageName === "Responded" ||
        stageName === "Interview" ||
        stageName === "Discarded" ||
        stageName === "SKIP") && (
        <section className="rounded-lg border border-border bg-card p-4">
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Stage notes
          </h3>
          <textarea
            value={stageNotes}
            onChange={(e) => setStageNotes(e.target.value)}
            rows={4}
            placeholder={
              stageName === "Applied"
                ? "Application date, portal used, confirmation…"
                : stageName === "Interview"
                  ? "Round type, interviewer, debrief…"
                  : "Notes for this stage…"
            }
            className="w-full rounded-md border border-border bg-bg px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-brand"
          />
          <div className="mt-2 flex justify-end">
            <button
              type="button"
              onClick={() => saveNotesMutation.mutate()}
              disabled={saveNotesMutation.isPending}
              className="rounded-md border border-border px-3 py-1.5 text-xs font-medium hover:bg-muted/20 disabled:opacity-50"
            >
              {saveNotesMutation.isPending ? "Saving…" : "Save notes"}
            </button>
          </div>
        </section>
      )}

      {stageName === "Rejected" && (
        <section className="rounded-lg border border-border bg-card p-4">
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Rejection reason
          </h3>
          <textarea
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
            rows={3}
            placeholder="What feedback did you receive?"
            className="w-full rounded-md border border-border bg-bg px-3 py-2 text-sm"
          />
          <div className="mt-2 flex justify-end">
            <button
              type="button"
              onClick={() => saveNotesMutation.mutate()}
              disabled={saveNotesMutation.isPending}
              className="rounded-md border border-border px-3 py-1.5 text-xs font-medium hover:bg-muted/20"
            >
              Save
            </button>
          </div>
        </section>
      )}

      {stageName === "Offer" && (
        <section className="rounded-lg border border-border bg-card p-4">
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Offer details (JSON)
          </h3>
          <textarea
            value={offerDetails}
            onChange={(e) => setOfferDetails(e.target.value)}
            rows={5}
            placeholder='{"base": 150000, "equity": "0.1%", "startDate": "2026-04-01"}'
            className="w-full rounded-md border border-border bg-bg px-3 py-2 font-mono text-xs"
          />
          <div className="mt-2 flex justify-end">
            <button
              type="button"
              onClick={() => saveNotesMutation.mutate()}
              disabled={saveNotesMutation.isPending}
              className="rounded-md border border-border px-3 py-1.5 text-xs font-medium hover:bg-muted/20"
            >
              Save offer details
            </button>
          </div>
        </section>
      )}

      {stageActions.length > 0 && (
        <section className="rounded-lg border border-border bg-card p-4">
          <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Stage history
          </h3>
          <div className="space-y-2">
            {stageActions
              .slice()
              .reverse()
              .slice(0, 8)
              .map((action) => (
                <div
                  key={action.actionId}
                  className="rounded-md border border-border bg-muted/10 px-3 py-2 text-xs"
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-medium text-foreground">{action.type}</span>
                    <span className="text-muted-foreground">
                      {new Date(action.createdAt).toLocaleString()}
                    </span>
                  </div>
                  {action.metadata?.promptKind != null && (
                    <p className="mt-0.5 text-muted-foreground">
                      Template: {String(action.metadata.promptKind)}
                      {typeof action.metadata.score === "number"
                        ? ` · ${action.metadata.score}/5`
                        : ""}
                    </p>
                  )}
                  {action.result && (
                    <p className="mt-1 line-clamp-2 text-muted-foreground">{action.result}</p>
                  )}
                </div>
              ))}
          </div>
        </section>
      )}

      {STAGE_TRANSITIONS[stageName].length > 0 && (
        <section className="rounded-lg border border-dashed border-border p-4">
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Move to next stage
          </h3>
          <div className="flex flex-wrap gap-2">
            {STAGE_TRANSITIONS[stageName].map((target) => (
              <button
                key={target}
                type="button"
                onClick={() => {
                  if (window.confirm(`Move this pipeline to “${target}”?`)) {
                    advanceMutation.mutate(target);
                  }
                }}
                disabled={advanceMutation.isPending}
                className={cn(
                  "rounded-md px-3 py-2 text-xs font-medium transition",
                  target === "Applied"
                    ? "bg-brand text-brand-foreground hover:opacity-90"
                    : "border border-border text-foreground hover:bg-muted/20",
                )}
              >
                → {target}
              </button>
            ))}
          </div>
          {stageName === "Evaluated" && displayScore !== undefined && displayScore < 3.5 && (
            <p className="mt-2 text-xs text-amber-600 dark:text-amber-400">
              Score is below 3.5 — consider SKIP or Discarded unless you have a strong reason to apply.
            </p>
          )}
        </section>
      )}

      <PromptPreviewModal
        kind={previewPromptKind}
        open={previewPromptKind !== null}
        onClose={() => setPreviewPromptKind(null)}
      />
    </div>
  );
}
